"use client";

import React, { forwardRef } from "react";
import { AlertCircle, ShoppingCart } from "lucide-react";
import { Button } from "../button";
import { cn } from "../../lib/utils";
import type { RequestToBookMixNotice } from "../../lib/checkout/request-to-book";

/** Why the host refused the last basket choice, and how the customer can fix it. */
export type BasketChoiceNotice = {
  title: string;
  message: string;
  /** Button under the message (e.g. "Open basket"). Omitted = text only. */
  action?: { label: string; onClick: () => void };
};

/**
 * A request-to-book refusal as every surface shows it: its wording, and
 * "Open basket" when removing basket items is the fix and the host can open
 * the basket (`openBasket` omitted: no basket on this surface).
 */
export function basketChoiceNoticeFor(
  notice: RequestToBookMixNotice,
  openBasket?: () => void,
): BasketChoiceNotice {
  return {
    title: notice.title,
    message: notice.message,
    action:
      notice.resolvableInBasket && openBasket
        ? { label: "Open basket", onClick: openBasket }
        : undefined,
  };
}

/**
 * An inline refusal next to the buttons that were just refused (e.g. a
 * request-to-book ticket that can't share the basket), persistent until the
 * host clears it. Rendered by the basket choice step, and by surfaces with
 * their own add/buy buttons.
 */
export const BasketConflictNotice = forwardRef<
  HTMLDivElement,
  { notice: BasketChoiceNotice; className?: string }
>(function BasketConflictNotice({ notice, className }, ref) {
  return (
    <div
      ref={ref}
      role="alert"
      className={cn(
        "flex items-start gap-3 p-4 rounded-lg border border-destructive/30 bg-destructive/5 text-left",
        className,
      )}
    >
      <AlertCircle className="w-5 h-5 text-destructive mt-0.5 shrink-0" aria-hidden="true" />
      <div className="space-y-1.5">
        <p className="text-sm font-semibold text-foreground">{notice.title}</p>
        <p className="text-sm text-muted-foreground">{notice.message}</p>
        {notice.action && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={notice.action.onClick}
          >
            <ShoppingCart aria-hidden="true" />
            {notice.action.label}
          </Button>
        )}
      </div>
    </div>
  );
});
